import { useEffect, useMemo, useRef, useState } from "react";
import { clsx } from "clsx";
import {
  ArrowRight, ChevronLeft, ChevronRight, Copy, Highlighter, Loader2, MousePointer2,
  Pencil, Save, Square, SquareSlash, TextCursorInput, Trash2, Type, Undo2, X,
} from "lucide-react";
import { Button } from "@/components/ui/Button";
import { pageImageUrl, usePageObjects, usePageTextRuns, useToolCatalog } from "../api/pdfApi";
import type { PdfDocument, PdfPageObject, PdfShape, PdfTextRun } from "../types";

type Kind = PdfShape["type"] | "edit" | "select";

const TOOLS: { kind: Kind; label: string; icon: typeof Square; hint: string }[] = [
  { kind: "select", label: "Zaznacz", icon: MousePointer2,
    hint: "Zaznacz elementy (Shift dokłada), przesuń je myszą, kopiuj Ctrl+C / Ctrl+V, usuń Delete" },
  { kind: "edit", label: "Popraw tekst", icon: TextCursorInput,
    hint: "Kliknij w istniejący tekst i przepisz go — działa też na skanie (OCR)" },
  { kind: "text", label: "Notatka", icon: Type, hint: "Kliknij w miejscu, w którym ma stanąć napis" },
  { kind: "highlight", label: "Zakreślacz", icon: Highlighter, hint: "Przeciągnij po tekście" },
  { kind: "box", label: "Ramka", icon: Square, hint: "Obrys obszaru" },
  { kind: "arrow", label: "Strzałka", icon: ArrowRight, hint: "Przeciągnij od–do" },
  { kind: "redact", label: "Zaczernij", icon: SquareSlash, hint: "Treść pod spodem znika trwale" },
];

const COLORS = ["#111111", "#dc1f1f", "#f5c400", "#1f7ddc", "#18a558"];
// Wartości z serwera (services/ops.py) — podgląd rysuje DOKŁADNIE to samo.
const BOX_PT = 1.5;
const ARROW_PT = 2;
const ARROW_HEAD_PT = 10;
const MIN_SIZE_PT = 4;

/**
 * Przesunięcie w granicach rozsądku: obiekt może wyjechać poza kartkę, byle
 * został z niego widoczny pasek. Poprzednia reguła („całość musi zostać na
 * stronie") ZEROWAŁA ruch obiektu wielkości strony — pełnostronicowego skanu
 * nie dało się ruszyć w ogóle (zmierzone w przeglądarce 30.08.2026).
 */
export function clampMove(pos: number, size: number, delta: number): number {
  const skraj = 0.05;
  return Math.max(-(pos + size) + skraj, Math.min(1 - pos - skraj, delta));
}
const SIZES = [6, 7, 8, 9, 10, 11, 12, 14, 16, 18, 20, 24, 28, 32, 40, 48];
const DEFAULT_SIZE = 12;

let counter = 0;
const nextId = () => `s${Date.now()}-${counter++}`;

interface PendingText {
  x: number;
  y: number;
  h: number;
  value: string;
  /** Ustawione, gdy poprawiamy istniejącą linię dokumentu. */
  run?: PdfTextRun;
  /** Ustawione, gdy poprawiamy TREŚĆ już dodanej notatki. */
  shapeId?: string;
}

/**
 * Edytor: adnotacje NAD stroną (zakreślacz, ramka, strzałka, notatka, podpis,
 * zaczernienie) oraz POPRAWIANIE TREŚCI, która już na stronie jest.
 *
 * Rysuje się po obrazie strony wyrenderowanym przez serwer — tym samym, który
 * serwer potem stempluje, więc to, co widać, jest tym, co wyjdzie w pliku.
 *
 * Dwie rzeczy, których nie widać, a które decydują o użyteczności:
 * • notatkę wpisuje się TAM, gdzie się kliknęło (pole tekstowe staje w tym miejscu),
 *   a nie w pasku narzędzi — pierwsza wersja wymagała wpisania treści z góry;
 * • „Popraw tekst” bierze linie z pliku (warstwa tekstowa) albo z OCR (skan) i po
 *   zapisie RASTERYZUJE poprawianą stronę, żeby stara treść naprawdę zniknęła.
 */
export function AnnotationEditor({
  document: doc,
  ticket,
  onSave,
  confirmSave,
  saving,
}: {
  document: PdfDocument;
  ticket: string | undefined;
  onSave: (work: {
    page: number;
    edits: PdfTextRun[];
    shapes: PdfShape[];
    /** Obiekty strony do przestawienia/usunięcia (obrazy, rysunki). */
    objects: { id: string; page: number; dx?: number; dy?: number; remove?: boolean }[];
  }) => void;
  /** Pytanie przed zapisem (np. o utratę podpisu elektronicznego). Odmowa zostawia
   * naniesioną pracę w edytorze — zapis czyści ją od razu, nie czekając na serwer. */
  confirmSave?: () => Promise<boolean>;
  saving: boolean;
}) {
  const [page, setPage] = useState(1);
  const [kind, setKind] = useState<Kind>("edit");
  const [color, setColor] = useState(COLORS[0]);
  const [font, setFont] = useState("liberation-sans");
  const [fontSize, setFontSize] = useState(DEFAULT_SIZE);
  const [bold, setBold] = useState(false);
  const [italic, setItalic] = useState(false);
  const [shapes, setShapes] = useState<PdfShape[]>([]);
  const [edits, setEdits] = useState<PdfTextRun[]>([]);
  const [draft, setDraft] = useState<PdfShape | null>(null);
  const [pending, setPending] = useState<PendingText | null>(null);
  // Zaznaczenie wieloobiektowe: przesuwanie, kopiowanie i kasowanie grupy — jak
  // w edytorach PDF-ów, zamiast obsługi „po jednym elemencie".
  const [picked, setPicked] = useState<Set<string>>(new Set());
  // `tresc` = czy zakreślenie ma objąć także treść strony (Ctrl przy zaczynaniu).
  // Bez tego gumka łapała 25 rzeczy naraz i jeden Delete kasował tekst dokumentu.
  const [band, setBand] = useState<
    { x0: number; y0: number; x1: number; y1: number; tresc: boolean } | null
  >(null);
  const [clipboard, setClipboard] = useState<PdfShape[]>([]);
  // Przesunięcia linii DOKUMENTU (nie adnotacji): id linii → ułamek strony.
  // Backend dostaje je jako `dx`/`dy` przy poprawce i przesuwa napis w treści
  // strony tą samą czcionką — bez rasteryzacji.
  // Obiekty strony, które nie są tekstem (obrazy, rysunki, kreski tabel):
  // przesunięcie i usunięcie — tą samą drogą co tekst, chirurgią na potoku.
  const [objMoves, setObjMoves] = useState<
    Record<string, { dx: number; dy: number; page: number; obj: PdfPageObject }>
  >({});
  const [objGone, setObjGone] = useState<Record<string, { page: number; obj: PdfPageObject }>>({});
  const [moves, setMoves] = useState<
    Record<string, { dx: number; dy: number; page: number; run: PdfTextRun }>
  >({});
  // Element pod kursorem: bez tego nie widać, że w gotowy napis DA SIĘ kliknąć.
  const [hover, setHover] = useState<string | null>(null);
  // Linie tekstu jadące razem z chwyconym obiektem (siatka tabeli obejmuje swój
  // tekst — przesunięcie samej siatki rozjeżdżało tabelę, zgłoszenie 30.08.2026).
  const [carried, setCarried] = useState<Set<string>>(new Set());
  const moving = useRef<{
    from: { x: number; y: number };
    snapshot: PdfShape[];
    runs: Record<string, { dx: number; dy: number; page: number; run: PdfTextRun }>;
    objects: Record<string, { dx: number; dy: number; page: number; obj: PdfPageObject }>;
    /** Wspólna ramka chwyconej grupy — przycinamy RAZ, całość, bo przycinanie
     * każdego elementu osobno deformowało grupę przy krawędzi kartki (tabela
     * rozjeżdżała się: siatka jechała o 0,074, a jej dolne wiersze o 0,049). */
    box: { x0: number; y0: number; x1: number; y1: number };
  } | null>(null);
  const [imgLoading, setImgLoading] = useState(true);
  // Pole „skocz do strony" — trzymane osobno, żeby dało się wpisać liczbę
  // dwucyfrową bez przeskakiwania podglądu po każdej cyfrze.
  const [pageInput, setPageInput] = useState("1");
  const boxRef = useRef<HTMLDivElement>(null);
  const pendingRef = useRef<HTMLInputElement>(null);
  const start = useRef<{ x: number; y: number } | null>(null);

  // Lista krojów pochodzi z serwera: pokazujemy dokładnie te, którymi backend
  // potrafi napisać (i które są w obrazie), zamiast zgadywać we froncie.
  const { data: catalog } = useToolCatalog();
  const fonts = catalog?.fonts ?? [];
  const fontCss = (value: string | undefined) =>
    fonts.find((f) => f.value === (value ?? "liberation-sans"))?.css;

  const pages = doc.page_count || 1;
  // Wysokość pola notatki w ułamku strony: stopień pisma w punktach dzielony przez
  // wysokość TEJ strony (a nie przez założone A4) — 12 pkt ma być 12 pkt na każdym
  // formacie. Backend przelicza to z powrotem na punkty.
  const pageHeightPt = doc.page_height || 842;
  const noteHeight = (fontSize / 0.78) / pageHeightPt;
  const textMode = kind === "edit";
  const { data: runsData, isFetching: runsLoading } = usePageTextRuns(doc.id, page, textMode || kind === "select", doc.version_number);
  const { data: objectsData } = usePageObjects(doc.id, page, kind === "select", doc.version_number);

  const pageSrc = ticket ? pageImageUrl(doc.id, page, ticket, 1000, doc.version_number) : "";

  const jumpToPage = () => {
    const want = Number(pageInput);
    if (!Number.isFinite(want) || want < 1) return setPageInput(String(page));
    const next = Math.min(pages, Math.max(1, Math.round(want)));
    setPageInput(String(next));
    if (next !== page) setPage(next);
  };

  const pageShapes = useMemo(() => shapes.filter((s) => s.page === page), [shapes, page]);
  const editedIds = useMemo(() => new Set(edits.map((e) => e.id)), [edits]);

  useEffect(() => {
    setImgLoading(true);
    setPending(null);
    setPicked(new Set());
    setBand(null);
    setPageInput(String(page));
  }, [page, doc.id, doc.version_number]);

  // Poprawki dotyczą KONKRETNEJ strony — przy zmianie strony trzeba je najpierw
  // zapisać, więc nie pozwalamy im się mieszać między stronami.
  const editsPage = useRef(page);
  useEffect(() => {
    if (edits.length === 0) editsPage.current = page;
  }, [page, edits.length]);

  // Ognisko ustawiamy po zamontowaniu pola (nie `autoFocus`), żeby nie ścigać się
  // z domyślnym działaniem kliknięcia.
  useEffect(() => {
    if (!pending) return;
    const id = requestAnimationFrame(() => pendingRef.current?.focus());
    return () => cancelAnimationFrame(id);
  }, [pending?.x, pending?.y, pending?.run?.id]);

  /** Prostokąt otaczający kształt (ułamki strony) — do trafiania i do gumki. */
  const boundsOf = (sh: PdfShape) => {
    if (sh.type === "arrow") {
      const x2 = sh.x2 ?? sh.x;
      const y2 = sh.y2 ?? sh.y;
      return {
        x0: Math.min(sh.x, x2), y0: Math.min(sh.y, y2),
        x1: Math.max(sh.x, x2), y1: Math.max(sh.y, y2),
      };
    }
    if (sh.type === "text") {
      const h = sh.h ?? noteHeight;
      // Szerokość napisu szacujemy z długości tekstu. Zaniżona sprawia, że
      // kliknięcie w prawą część notatki „nie trafia" i nic się nie zaznacza,
      // więc bierzemy miarę z zapasem — lepiej trafić za szeroko niż nie trafić.
      const w = Math.min(0.95, (sh.text?.length ?? 4) * h * 0.55);
      return { x0: sh.x, y0: sh.y - h * 0.3, x1: sh.x + w, y1: sh.y + h * 1.2 };
    }
    return { x0: sh.x, y0: sh.y, x1: sh.x + (sh.w ?? 0), y1: sh.y + (sh.h ?? 0) };
  };

  const pageRuns = runsData?.runs ?? [];
  const pageObjects = (objectsData?.objects ?? []).filter((o) => !objGone[o.id]);
  const objMoveOf = (id: string) => objMoves[id] ?? { dx: 0, dy: 0 };
  const objBounds = (o: PdfPageObject) => {
    const m = objMoveOf(o.id);
    return { x0: o.x + m.dx, y0: o.y + m.dy, x1: o.x + o.w + m.dx, y1: o.y + o.h + m.dy };
  };
  const objMovedHere = Object.keys(objMoves).filter(
    (id) => (objMoves[id].dx || objMoves[id].dy) && objMoves[id].page === page,
  );
  const objGoneHere = Object.keys(objGone).filter((id) => objGone[id].page === page);
  const objWork = Object.keys(objMoves).filter((id) => objMoves[id].dx || objMoves[id].dy)
    .concat(Object.keys(objGone));
  const moveOf = (id: string) => moves[id] ?? { dx: 0, dy: 0 };
  const movedIds = Object.keys(moves).filter((id) => moves[id].dx || moves[id].dy);
  // Podgląd pokazuje WYŁĄCZNIE robotę z tej strony: poprawka zrobiona na jednej
  // kartce nie ma prawa malować się na następnych (zgłoszenie 30.08.2026).
  const movedHere = movedIds.filter((id) => moves[id].page === page);
  const editsHere = edits.filter((e) => (e.page ?? editsPage.current) === page);
  const runBounds = (r: PdfTextRun) => {
    const m = moveOf(r.id);
    return { x0: r.x + m.dx, y0: r.y + m.dy, x1: r.x + r.w + m.dx, y1: r.y + r.h + m.dy };
  };

  const hitTest = (p: { x: number; y: number }) => {
    const pad = 0.006;
    return [...pageShapes].reverse().find((sh) => {
      const b = boundsOf(sh);
      return p.x >= b.x0 - pad && p.x <= b.x1 + pad && p.y >= b.y0 - pad && p.y <= b.y1 + pad;
    });
  };

  /** Linia dokumentu pod kursorem — w trybie zaznaczania chwyta się ją tak samo
   * jak adnotację, więc tekst da się PRZESTAWIĆ, a nie tylko przepisać. */
  const hitRun = (p: { x: number; y: number }) => {
    const pad = 0.004;
    return [...pageRuns].reverse().find((r) => {
      const b = runBounds(r);
      return p.x >= b.x0 - pad && p.x <= b.x1 + pad && p.y >= b.y0 - pad && p.y <= b.y1 + pad;
    });
  };

  /** Obiekt strony pod kursorem (sam w sobie — wybór między rodzajami robi `pickAt`). */
  const hitObject = (p: { x: number; y: number }) => {
    const pad = 0.004;
    return [...pageObjects].reverse().find((o) => {
      const b = objBounds(o);
      return p.x >= b.x0 - pad && p.x <= b.x1 + pad && p.y >= b.y0 - pad && p.y <= b.y1 + pad;
    });
  };

  const moveObjects = (
    ids: Set<string>, dx: number, dy: number,
    from: Record<string, { dx: number; dy: number }>,
  ) =>
    setObjMoves((prev) => {
      const next = { ...prev };
      pageObjects.forEach((o) => {
        if (!ids.has(o.id)) return;
        const base = from[o.id] ?? { dx: 0, dy: 0 };
        next[o.id] = { dx: base.dx + dx, dy: base.dy + dy, page, obj: o };
      });
      return next;
    });

  const moveRuns = (
    ids: Set<string>, dx: number, dy: number,
    from: Record<string, { dx: number; dy: number }>,
  ) =>
    setMoves((prev) => {
      const next = { ...prev };
      pageRuns.forEach((r) => {
        if (!ids.has(r.id)) return;
        const base = from[r.id] ?? { dx: 0, dy: 0 };
        next[r.id] = { dx: base.dx + dx, dy: base.dy + dy, page, run: r };
      });
      return next;
    });

  /** Linie tekstu w całości mieszczące się w obiekcie — jadą z nim. */
  const runsInside = (o: PdfPageObject) => {
    const b = objBounds(o);
    const t = 0.006;
    return pageRuns.filter((r) => {
      const rb = runBounds(r);
      return rb.x0 >= b.x0 - t && rb.x1 <= b.x1 + t && rb.y0 >= b.y0 - t && rb.y1 <= b.y1 + t;
    });
  };

  /** Co złapie kliknięcie: adnotacja, linia tekstu czy obiekt strony. Wygrywa
   * NAJMNIEJSZY z tego, co jest pod kursorem — inaczej ramka tabeli obejmująca
   * napis nie dałaby się chwycić nigdy (a przy sztywnym pierwszeństwie tekstu
   * przeciągnięcie ramki przestawiało napis). Przy remisie: własna adnotacja,
   * potem tekst, na końcu grafika. */
  const pickAt = (p: { x: number; y: number }) => {
    const area = (b: { x0: number; y0: number; x1: number; y1: number }) =>
      Math.max(0.000001, (b.x1 - b.x0) * (b.y1 - b.y0));
    const shape = hitTest(p);
    const run = hitRun(p);
    const object = hitObject(p);
    const candidates = [
      shape ? { kind: "shape" as const, id: shape.id, shape, area: area(boundsOf(shape)), rank: 0 } : null,
      run ? { kind: "run" as const, id: run.id, run, area: area(runBounds(run)), rank: 1 } : null,
      object ? { kind: "object" as const, id: object.id, object, area: area(objBounds(object)), rank: 2 } : null,
    ].filter(Boolean) as {
      kind: "shape" | "run" | "object"; id: string; area: number; rank: number;
      shape?: PdfShape; run?: PdfTextRun; object?: PdfPageObject;
    }[];
    candidates.sort((a, b) => (a.area - b.area) || (a.rank - b.rank));
    return candidates[0];
  };

  /** Ramka obejmująca wszystko, co jedzie razem. */
  const groupBounds = (ids: Set<string>) => {
    const boxes = [
      ...pageShapes.filter((sh) => ids.has(sh.id)).map(boundsOf),
      ...pageRuns.filter((r) => ids.has(r.id)).map(runBounds),
      ...pageObjects.filter((o) => ids.has(o.id)).map(objBounds),
    ];
    if (!boxes.length) return { x0: 0, y0: 0, x1: 1, y1: 1 };
    return {
      x0: Math.min(...boxes.map((b) => b.x0)), y0: Math.min(...boxes.map((b) => b.y0)),
      x1: Math.max(...boxes.map((b) => b.x1)), y1: Math.max(...boxes.map((b) => b.y1)),
    };
  };

  const moveShapes = (ids: Set<string>, dx: number, dy: number, from: PdfShape[]) =>
    setShapes((all) =>
      all.map((sh) => {
        if (!ids.has(sh.id)) return sh;
        const origin = from.find((o) => o.id === sh.id) ?? sh;
        return {
          ...sh,
          x: origin.x + dx,
          y: origin.y + dy,
          ...(origin.x2 !== undefined ? { x2: origin.x2 + dx } : {}),
          ...(origin.y2 !== undefined ? { y2: origin.y2 + dy } : {}),
        };
      }),
    );

  const point = (e: React.MouseEvent) => {
    const box = boxRef.current?.getBoundingClientRect();
    if (!box) return { x: 0, y: 0 };
    return {
      x: Math.min(1, Math.max(0, (e.clientX - box.left) / box.width)),
      y: Math.min(1, Math.max(0, (e.clientY - box.top) / box.height)),
    };
  };

  /** Zaznaczyłeś napis — paski mają pokazać JEGO krój, kolor i stopień pisma,
   * a nie ostatnio wybrane. Inaczej „zmiana koloru" zaczyna się od zgadywania. */
  const adoptStyle = (sh: PdfShape) => {
    if (sh.color) setColor(sh.color);
    if (sh.type !== "text") return;
    if (sh.font) setFont(sh.font);
    if (sh.h) setFontSize(Math.max(6, Math.round(sh.h * pageHeightPt * 0.78)));
    setBold(!!sh.bold);
    setItalic(!!sh.italic);
  };

  /** Otwiera notatkę do poprawienia — z podwójnego kliknięcia, z kliknięcia
   * narzędziem „Notatka" i z ołówka na liście. Jedna droga, trzy wejścia. */
  const editNote = (sh: PdfShape) => {
    commitPending();
    setPicked(new Set([sh.id]));
    adoptStyle(sh);
    setPending({ x: sh.x, y: sh.y, h: sh.h ?? noteHeight, value: sh.text ?? "", shapeId: sh.id });
  };

  const down = (e: React.MouseEvent) => {
    if (pending) return; // najpierw domknij otwarte pole tekstowe
    // Bez tego domyślne działanie wciśnięcia myszy przenosi zaraz potem ognisko
    // na stronę, świeżo otwarte pole notatki traci fokus i samo się zamyka —
    // notatki nie dało się w ogóle wpisać (zgłoszenie 2026-08-29). Przy okazji
    // znika zaznaczanie tekstu podczas rysowania ramek.
    e.preventDefault();
    const p = point(e);
    if (textMode) return; // w trybie poprawiania klikamy w gotowe ramki, nie rysujemy
    if (kind === "select") {
      const pick = pickAt(p);
      if (pick) {
        const next = new Set(picked);
        if (e.shiftKey || e.ctrlKey || e.metaKey) {
          next.has(pick.id) ? next.delete(pick.id) : next.add(pick.id);
        } else if (!next.has(pick.id)) {
          next.clear();
          next.add(pick.id);
        }
        // Zaznaczona adnotacja podstawia swój styl w paskach; linia i obiekt
        // strony niczego nie podstawiają, bo ich stylu tu nie zmieniamy.
        if (pick.shape) adoptStyle(pick.shape);
        // Chwytasz siatkę tabeli — jedzie z nią jej tekst. Kasowanie obejmuje
        // TYLKO zaznaczone, więc usunięcie siatki nie kasuje napisów.
        const carriedNow = pick.object ? runsInside(pick.object).map((r) => r.id) : [];
        setCarried(new Set(carriedNow));
        setPicked(next);
        const grupa = new Set([...next, ...carriedNow]);
        moving.current = {
          from: p,
          snapshot: shapes.filter((sh) => next.has(sh.id)),
          runs: { ...moves },
          objects: { ...objMoves },
          box: groupBounds(grupa),
        };
      } else {
        if (!e.shiftKey) setPicked(new Set());
        setBand({
          x0: p.x, y0: p.y, x1: p.x, y1: p.y,
          tresc: e.ctrlKey || e.metaKey,
        });
      }
      return;
    }
    if (kind === "text") {
      // Klik w GOTOWĄ notatkę poprawia ją, zamiast kłaść drugą na wierzchu —
      // narzędziem, którym się ją zrobiło, musi dać się ją też zmienić.
      const hit = hitTest(p);
      if (hit && hit.type === "text") {
        editNote(hit);
        return;
      }
      setPending({ x: p.x, y: p.y, h: noteHeight, value: "" });
      return;
    }
    start.current = p;
    setDraft({
      id: "draft", page, type: kind as PdfShape["type"],
      x: p.x, y: p.y, w: 0, h: 0, x2: p.x, y2: p.y, color,
    });
  };

  /** Podwójne kliknięcie w notatkę = poprawa jej treści. Dodana notatka musi
   * dać się zmienić, a nie tylko usunąć i napisać od nowa. */
  const openNoteForEdit = (e: React.MouseEvent) => {
    if (textMode) return;
    const hit = hitTest(point(e));
    if (!hit || hit.type !== "text") return;
    e.preventDefault();
    e.stopPropagation();
    editNote(hit);
  };

  const move = (e: React.MouseEvent) => {
    if (kind === "select") {
      const p = point(e);
      if (!moving.current && !band) {
        setHover(pickAt(p)?.id ?? null);
      }
      if (moving.current) {
        const dx = p.x - moving.current.from.x;
        const dy = p.y - moving.current.from.y;
        const ruszane = new Set([...picked, ...carried]);
        const box = moving.current.box;
        const gdx = clampMove(box.x0, box.x1 - box.x0, dx);
        const gdy = clampMove(box.y0, box.y1 - box.y0, dy);
        moveShapes(ruszane, gdx, gdy, moving.current.snapshot);
        moveRuns(ruszane, gdx, gdy, moving.current.runs);
        moveObjects(ruszane, gdx, gdy, moving.current.objects);
      } else if (band) {
        setBand({ ...band, x1: p.x, y1: p.y });
      }
      return;
    }
    if (kind === "text") setHover(hitTest(point(e))?.type === "text" ? "note" : null);
    if (!start.current || !draft) return;
    const p = point(e);
    const a = start.current;
    setDraft(
      kind === "arrow"
        ? { ...draft, x: a.x, y: a.y, x2: p.x, y2: p.y }
        : {
            ...draft,
            x: Math.min(a.x, p.x), y: Math.min(a.y, p.y),
            w: Math.abs(p.x - a.x), h: Math.abs(p.y - a.y),
          },
    );
  };

  const up = () => {
    if (kind === "select") {
      if (band) {
        const x0 = Math.min(band.x0, band.x1);
        const x1 = Math.max(band.x0, band.x1);
        const y0 = Math.min(band.y0, band.y1);
        const y1 = Math.max(band.y0, band.y1);
        if (x1 - x0 > 0.005 || y1 - y0 > 0.005) {
          const inside = pageShapes.filter((sh) => {
            const b = boundsOf(sh);
            return b.x1 >= x0 && b.x0 <= x1 && b.y1 >= y0 && b.y0 <= y1;
          });
          // Domyślnie gumka bierze TYLKO własne adnotacje. Treść strony (linie
          // tekstu, obrazy, rysunki) dochodzi dopiero z Ctrl — inaczej jeden
          // Delete po zakreśleniu wyczyściłby pół dokumentu.
          const lines = band.tresc
            ? pageRuns.filter((r) => {
                const b = runBounds(r);
                return b.x1 >= x0 && b.x0 <= x1 && b.y1 >= y0 && b.y0 <= y1;
              })
            : [];
          const objs = band.tresc
            ? pageObjects.filter((o) => {
                const b = objBounds(o);
                return b.x1 >= x0 && b.x0 <= x1 && b.y1 >= y0 && b.y0 <= y1;
              })
            : [];
          setPicked((prev) => new Set([
            ...prev, ...inside.map((sh) => sh.id), ...lines.map((r) => r.id),
            ...objs.map((o) => o.id),
          ]));
        }
        setBand(null);
      }
      moving.current = null;
      return;
    }
    if (draft) {
      const big =
        draft.type === "arrow"
          ? Math.hypot((draft.x2 ?? 0) - draft.x, (draft.y2 ?? 0) - draft.y) > 0.02
          : (draft.w ?? 0) > 0.01 && (draft.h ?? 0) > 0.005;
      if (big) {
        const shape: PdfShape = { ...draft, id: nextId() };
        setShapes((s) => [...s, shape]);
      }
    }
    setDraft(null);
    start.current = null;
  };

  const commitPending = () => {
    if (!pending) return;
    const value = pending.value.trim();
    if (pending.shapeId) {
      // Poprawka treści istniejącej notatki: pusta treść = notatka znika.
      const id = pending.shapeId;
      setShapes((all) =>
        value
          ? all.map((sh) => (sh.id === id ? { ...sh, text: value } : sh))
          : all.filter((sh) => sh.id !== id),
      );
      setPending(null);
      return;
    }
    if (pending.run) {
      // Wyczyszczona linia to POLECENIE USUNIĘCIA napisu ze strony, a nie „brak
      // zmiany" — pusta poprawka wypadała wcześniej z zapisu i nic się nie działo
      // (zgłoszenie 30.08.2026: „jak dany tekst usunę w pełni, to nic się nie zmienia").
      const run = pending.run;
      setEdits((prev) => [
        ...prev.filter((e) => e.id !== run.id), { ...run, page, text: value },
      ]);
      setPending(null);
      return;
    }
    if (value) {
      setShapes((s) => [
        ...s,
        {
          id: nextId(), page, type: "text", x: pending.x, y: pending.y, h: pending.h,
          text: value, color, font, bold, italic,
        },
      ]);
    }
    setPending(null);
  };

  // Delete / Ctrl+C / Ctrl+V działają na zaznaczeniu — dopóki nie piszemy w polu.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null;
      if (el && ["INPUT", "TEXTAREA", "SELECT"].includes(el.tagName)) return;
      if (e.key === "Escape") {
        setCarried(new Set());
        return setPicked(new Set());
      }
      if ((e.key === "Delete" || e.key === "Backspace") && picked.size) {
        e.preventDefault();
        setShapes((all) => all.filter((sh) => !picked.has(sh.id)));
        // Obiekt strony (obraz, rysunek) znika z treści strony…
        const goneNow = pageObjects.filter((o) => picked.has(o.id));
        if (goneNow.length) {
          setObjGone((prev) => ({
            ...prev,
            ...Object.fromEntries(goneNow.map((o) => [o.id, { page, obj: o }])),
          }));
        }
        // …a linia tekstu przez poprawkę na pustą treść (stary napis wypada z potoku).
        const linesNow = pageRuns.filter((r) => picked.has(r.id));
        if (linesNow.length) {
          setEdits((prev) => [
            ...prev.filter((x) => !picked.has(x.id)),
            ...linesNow.map((r) => ({ ...r, page, text: "" })),
          ]);
        }
        setPicked(new Set());
        return;
      }
      const mod = e.ctrlKey || e.metaKey;
      if (mod && e.key.toLowerCase() === "c" && picked.size) {
        setClipboard(shapes.filter((sh) => picked.has(sh.id)));
      }
      if (mod && e.key.toLowerCase() === "v" && clipboard.length) {
        e.preventDefault();
        const copies = clipboard.map((sh) => ({
          ...sh,
          id: nextId(),
          page,
          x: Math.min(0.98, sh.x + 0.02),
          y: Math.min(0.98, sh.y + 0.02),
          ...(sh.x2 !== undefined ? { x2: sh.x2 + 0.02 } : {}),
          ...(sh.y2 !== undefined ? { y2: sh.y2 + 0.02 } : {}),
        }));
        setShapes((all) => [...all, ...copies]);
        setPicked(new Set(copies.map((c) => c.id)));
        setKind("select");
      }
      if (mod && e.key.toLowerCase() === "a" && pageShapes.length) {
        e.preventDefault();
        setPicked(new Set(pageShapes.map((sh) => sh.id)));
        setKind("select");
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [picked, clipboard, shapes, pageShapes, page]);

  // Ta sama miara, którą liczony jest klik (`point`), więc podgląd i trafienia
  // nie mogą się rozjechać; `clientHeight` bywa zerem przed pierwszym układem.
  const boxHeightPx =
    boxRef.current?.getBoundingClientRect().height || boxRef.current?.clientHeight || 1000;
  /** Punkt PDF → piksel podglądu. Tym samym przelicznikiem idzie stopień pisma,
   * grubość kreski i grot strzałki, więc podgląd pokazuje to, co wyjdzie w pliku,
   * niezależnie od powiększenia (serwer: kreska ramki 1,5 pkt, strzałka 2 pkt,
   * grot 10 pkt, najmniejsze pismo 4 pkt). */
  const ptPx = (pt: number) => (pt * boxHeightPx) / pageHeightPt;
  const fontPx = (h: number) => ptPx(Math.max(MIN_SIZE_PT, h * pageHeightPt * 0.78));

  const render = (s: PdfShape, ghost = false) => {
    const key = ghost ? "draft" : s.id;
    const common = "pointer-events-none absolute";
    if (s.type === "arrow") {
      return (
        <svg key={key} className={`${common} inset-0 h-full w-full overflow-visible`}>
          <defs>
            {/* Grot w jednostkach grubości kreski: długość 10 pkt, rozwarcie
                0,4 rad — jeden do jednego z `beginPath` w services/ops.py. */}
            <marker
              id={`head-${key}`}
              markerUnits="strokeWidth"
              markerWidth={ARROW_HEAD_PT / ARROW_PT}
              markerHeight={(2 * ARROW_HEAD_PT * Math.sin(0.4)) / ARROW_PT}
              refX={ARROW_HEAD_PT / ARROW_PT}
              refY={(ARROW_HEAD_PT * Math.sin(0.4)) / ARROW_PT}
              orient="auto"
            >
              <path
                d={`M0,0 L${ARROW_HEAD_PT / ARROW_PT},${(ARROW_HEAD_PT * Math.sin(0.4)) / ARROW_PT} L0,${(2 * ARROW_HEAD_PT * Math.sin(0.4)) / ARROW_PT} z`}
                fill={s.color}
              />
            </marker>
          </defs>
          <line
            x1={`${s.x * 100}%`} y1={`${s.y * 100}%`}
            x2={`${(s.x2 ?? 0) * 100}%`} y2={`${(s.y2 ?? 0) * 100}%`}
            stroke={s.color} strokeWidth={ptPx(ARROW_PT)} markerEnd={`url(#head-${key})`}
          />
        </svg>
      );
    }
    const style: React.CSSProperties = {
      left: `${s.x * 100}%`, top: `${s.y * 100}%`,
      width: `${(s.w ?? 0) * 100}%`, height: `${(s.h ?? 0) * 100}%`,
    };
    if (s.type === "text") {
      return (
        <span
          key={key}
          className={`${common} whitespace-pre leading-none`}
          style={{
            left: style.left, top: style.top, color: s.color,
            fontSize: fontPx(s.h ?? noteHeight),
            fontFamily: fontCss(s.font),
            fontWeight: s.bold ? 700 : 400,
            fontStyle: s.italic ? "italic" : "normal",
          }}
        >
          {s.text}
        </span>
      );
    }
    return (
      <div
        key={key}
        className={common}
        style={{
          ...style,
          background: s.type === "redact" ? "#000" : s.type === "highlight" ? `${s.color}59` : "transparent",
          border: s.type === "box" ? `${ptPx(BOX_PT)}px solid ${s.color}` : undefined,
          opacity: ghost ? 0.6 : 1,
        }}
      />
    );
  };

  /** Zmiana ustawienia: dla nowych elementów ORAZ dla tych zaznaczonych — inaczej
   * styl dało się wybrać tylko przed narysowaniem, a potem już nic. */
  const applyStyle = (patch: Partial<PdfShape>) => {
    if (picked.size === 0) return;
    setShapes((all) => all.map((sh) => (picked.has(sh.id) ? { ...sh, ...patch } : sh)));
  };

  /** Poprawki treści i przestawione linie idą jednym kanałem: backend dostaje
   * STARE współrzędne linii (po nich ją znajduje) plus `dx`/`dy`. */
  const workEdits = (): PdfTextRun[] => {
    const byId = new Map(edits.map((e) => [e.id, { ...e }]));
    movedIds.forEach((id) => {
      const m = moves[id];
      const base = byId.get(id) ?? { ...m.run, page: m.page };
      byId.set(id, { ...base, page: base.page ?? m.page, dx: m.dx, dy: m.dy });
    });
    return [...byId.values()];
  };

  /** Przesunięcia i usunięcia obiektów strony — każde ze swoją stroną. */
  const workObjects = () => [
    ...Object.entries(objMoves)
      .filter(([, m]) => m.dx || m.dy)
      .map(([id, m]) => ({ id, page: m.page, dx: m.dx, dy: m.dy })),
    ...Object.entries(objGone).map(([id, g]) => ({ id, page: g.page, remove: true })),
  ];

  const hasWork =
    shapes.length > 0 || edits.length > 0 || movedIds.length > 0 || objWork.length > 0;
  // Paski nie chowają się przy zmianie narzędzia, tylko gasną. Znikające pola
  // zmieniały wysokość nagłówka i podgląd strony podskakiwał o ponad 100 px —
  // klik w gotową notatkę trafiał wtedy obok („notatki dalej nie da się
  // edytować", 29.08.2026). Wysokość nagłówka ma być stała.
  const colorActive = !textMode;
  const styleActive = !textMode && (kind === "text" || picked.size > 0);
  const hint = textMode
    ? runsLoading
      ? "Szukam tekstu na stronie…"
      : runsData?.source === "ocr"
        ? `Skan bez warstwy tekstowej — tekst odczytany OCR-em (${runsData.runs.length} linii). Kliknij w linię, żeby ją poprawić; stary napis zostanie zakryty kolorem tła.`
        : runsData?.source === "vector"
          ? `Kliknij w linię, żeby ją przepisać (${runsData.runs.length} linii). Stary napis znika z treści strony, nowy wchodzi tym samym krojem — strona zostaje wektorowa.`
          : "Na tej stronie nie znaleziono tekstu do poprawienia."
    : kind === "select"
      ? "Chwyć linię tekstu, obraz, rysunek albo adnotację i przeciągnij — treść strony przestawia się naprawdę. Zakreślenie łapie Twoje adnotacje, a z wciśniętym Ctrl także treść strony. Shift dokłada, Delete usuwa zaznaczone, Ctrl+C / Ctrl+V kopiuje."
      : kind === "text"
        ? "Kliknij w miejscu, w którym ma stanąć napis. W gotową notatkę kliknij, żeby ją poprawić."
        : TOOLS.find((t) => t.kind === kind)?.hint ?? "";

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex flex-wrap items-center gap-1.5 border-b border-[var(--color-border)] bg-[var(--color-bg-elevated)] px-2 py-1.5">
        {TOOLS.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.kind}
              type="button"
              title={t.hint}
              onClick={() => {
                commitPending();
                setKind(t.kind);
              }}
              aria-pressed={kind === t.kind}
              className={clsx(
                "flex items-center gap-1 border px-2 py-1 text-[11px] transition-colors duration-100",
                kind === t.kind
                  ? "border-[var(--color-primary)] bg-[var(--color-primary-dim)] text-[var(--color-primary)]"
                  : "border-[var(--color-border)] bg-[var(--color-bg-surface)] text-[var(--color-text-secondary)] hover:border-[var(--color-border-strong)]",
              )}
            >
              <Icon size={13} /> {t.label}
            </button>
          );
        })}

        <span className="mx-1 h-4 w-px bg-[var(--color-border)]" />
        {COLORS.map((c) => (
          <button
            key={c}
            type="button"
            disabled={!colorActive}
            aria-label={`Kolor ${c}`}
            onClick={() => {
              setColor(c);
              applyStyle({ color: c });
            }}
            style={{ background: c }}
            className={clsx(
              "h-5 w-5 border disabled:opacity-30",
              color === c ? "border-[var(--color-text-primary)]" : "border-[var(--color-border)]",
            )}
          />
        ))}

          <select
            value={font}
            disabled={!styleActive}
            onChange={(e) => {
              setFont(e.target.value);
              applyStyle({ font: e.target.value });
            }}
            aria-label="Krój notatki"
            className="input-base min-w-[7rem] max-w-[13rem] flex-1 py-1 text-[11px] disabled:opacity-40"
          >
            {fonts.map((f) => (
              <option key={f.value} value={f.value}>
                {f.label}
              </option>
            ))}
          </select>
          <select
            value={fontSize}
            disabled={!styleActive}
            onChange={(e) => {
              const value = Number(e.target.value);
              setFontSize(value);
              applyStyle({ h: (value / 0.78) / pageHeightPt });
            }}
            aria-label="Wielkość notatki"
            className="input-base w-[4.75rem] shrink-0 py-1 text-[11px] disabled:opacity-40"
          >
            {SIZES.map((v) => (
              <option key={v} value={v}>
                {v} pkt
              </option>
            ))}
          </select>
          <button
            type="button" aria-label="Pogrubienie" aria-pressed={bold} disabled={!styleActive}
            onClick={() => {
              setBold(!bold);
              applyStyle({ bold: !bold });
            }}
            className={clsx(
              "border px-2 py-1 text-[11px] font-bold disabled:opacity-40",
              bold
                ? "border-[var(--color-primary)] bg-[var(--color-primary-dim)] text-[var(--color-primary)]"
                : "border-[var(--color-border)] bg-[var(--color-bg-surface)] text-[var(--color-text-secondary)]",
            )}
          >
            B
          </button>
          <button
            type="button" aria-label="Kursywa" aria-pressed={italic} disabled={!styleActive}
            onClick={() => {
              setItalic(!italic);
              applyStyle({ italic: !italic });
            }}
            className={clsx(
              "border px-2 py-1 text-[11px] italic disabled:opacity-40",
              italic
                ? "border-[var(--color-primary)] bg-[var(--color-primary-dim)] text-[var(--color-primary)]"
                : "border-[var(--color-border)] bg-[var(--color-bg-surface)] text-[var(--color-text-secondary)]",
            )}
          >
            I
          </button>

        <span className="ml-1 flex items-center gap-1.5">
            <span
              title={picked.size > 0 ? `zaznaczone: ${picked.size}` : undefined}
              className="w-[9.5rem] shrink-0 truncate font-mono text-[11px] text-[var(--color-text-muted)]"
            >
              {picked.size > 0
                ? `zaznaczone: ${picked.size}${carried.size ? ` (+${carried.size} linii)` : ""}`
                : kind === "select"
                  ? "kliknij lub zakreśl elementy"
                  : "nic nie zaznaczono"}
            </span>
            <Button
              type="button" variant="ghost" size="sm" disabled={picked.size === 0}
              onClick={() => setClipboard(shapes.filter((sh) => picked.has(sh.id)))}
              title="Kopiuj (Ctrl+C)"
            >
              <Copy size={12} className="mr-1" /> Kopiuj
            </Button>
            <Button
              type="button" variant="ghost" size="sm" disabled={picked.size === 0}
              onClick={() => {
                setShapes((all) => all.filter((sh) => !picked.has(sh.id)));
                setPicked(new Set());
              }}
              title="Usuń zaznaczone (Delete)"
            >
              <Trash2 size={12} className="mr-1" /> Usuń
            </Button>
        </span>

        <span className="ml-auto flex items-center gap-1.5">
          <Button
            type="button" variant="ghost" size="sm"
            disabled={!hasWork}
            onClick={() => {
              const goneKeys = Object.keys(objGone);
              const objMoveKeys = Object.keys(objMoves).filter(
                (id) => objMoves[id].dx || objMoves[id].dy,
              );
              if (goneKeys.length) {
                const last = goneKeys[goneKeys.length - 1];
                setObjGone(({ [last]: _drop, ...rest }) => rest);
              } else if (objMoveKeys.length) {
                const last = objMoveKeys[objMoveKeys.length - 1];
                setObjMoves(({ [last]: _drop, ...rest }) => rest);
              } else if (movedIds.length) {
                const last = movedIds[movedIds.length - 1];
                setMoves(({ [last]: _drop, ...rest }) => rest);
              } else if (edits.length) {
                setEdits((e) => e.slice(0, -1));
              } else {
                setShapes((s) => s.slice(0, -1));
              }
            }}
          >
            <Undo2 size={12} className="mr-1" /> Cofnij
          </Button>
          <Button
            type="button" variant="ghost" size="sm" disabled={!hasWork}
            onClick={() => {
              setShapes([]);
              setEdits([]);
              setMoves({});
              setObjMoves({});
              setObjGone({});
            }}
          >
            <Trash2 size={12} className="mr-1" /> Wyczyść
          </Button>
          <Button
            type="button" size="sm" loading={saving} disabled={!hasWork}
            onClick={async () => {
              if (confirmSave && !(await confirmSave())) return;
              commitPending();
              onSave({
                page: editsPage.current, edits: workEdits(), shapes, objects: workObjects(),
              });
              setShapes([]);
              setEdits([]);
              setMoves({});
              setObjMoves({});
              setObjGone({});
            }}
          >
            <Save size={12} className="mr-1" /> Zapisz na pliku
          </Button>
        </span>
      </div>

      <div className="flex items-center justify-center gap-3 border-b border-[var(--color-border)] px-2 py-1">
        <button
          type="button" aria-label="Poprzednia strona" disabled={page <= 1}
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          className="text-[var(--color-text-muted)] disabled:opacity-30 hover:text-[var(--color-primary)]"
        >
          <ChevronLeft size={16} />
        </button>
        <span className="flex items-center gap-1 font-mono text-[11px] text-[var(--color-text-secondary)]">
          strona
          {/* Przy 96 stronach klikanie strzałką jest nie do przyjęcia — numer
              wpisuje się wprost. */}
          <input
            type="number"
            min={1}
            max={pages}
            value={pageInput}
            aria-label="Numer strony"
            onChange={(e) => setPageInput(e.target.value)}
            onFocus={(e) => e.currentTarget.select()}
            onBlur={jumpToPage}
            onKeyDown={(e) => {
              e.stopPropagation();
              if (e.key === "Enter") jumpToPage();
              if (e.key === "Escape") setPageInput(String(page));
            }}
            className="input-base w-14 px-1 py-0.5 text-center font-mono text-[11px]"
          />
          / {pages}
          {pageShapes.length > 0 && ` · ${pageShapes.length} elem.`}
          {edits.length > 0 && ` · ${edits.length} popr.`}
          {movedIds.length > 0 && ` · ${movedIds.length} przestaw.`}
          {objWork.length > 0 && ` · ${objWork.length} obiekt.`}
        </span>
        <button
          type="button" aria-label="Następna strona" disabled={page >= pages}
          onClick={() => setPage((p) => Math.min(pages, p + 1))}
          className="text-[var(--color-text-muted)] disabled:opacity-30 hover:text-[var(--color-primary)]"
        >
          <ChevronRight size={16} />
        </button>
      </div>

      <p
        title={hint}
        className="truncate border-b border-[var(--color-border)] px-3 py-1 text-[11px] text-[var(--color-text-muted)]"
      >
        {hint}
      </p>

      <div className="min-h-0 flex-1 overflow-auto bg-[var(--color-bg-overlay)] p-4">
        <div
          ref={boxRef}
          onMouseDown={down}
          onMouseMove={move}
          onMouseUp={up}
          onMouseLeave={up}
          onDoubleClick={openNoteForEdit}
          className={clsx(
            "relative mx-auto w-full max-w-[860px] select-none bg-white shadow-[4px_4px_0_var(--color-border-strong)]",
            hover && (kind === "select" || kind === "text")
              ? (kind === "select" ? "cursor-move" : "cursor-text")
              : textMode || kind === "select"
                ? "cursor-default"
                : "cursor-crosshair",
          )}
        >
          {ticket ? (
            <img
              src={pageSrc}
              alt={`Strona ${page}`}
              draggable={false}
              onLoad={() => setImgLoading(false)}
              onError={() => setImgLoading(false)}
              className="block w-full"
            />
          ) : (
            <div className="aspect-[1/1.41] w-full animate-pulse bg-[var(--color-bg-elevated)]" />
          )}

          {(imgLoading || runsLoading) && (
            <span
              className="absolute right-2 top-2 flex items-center gap-1 bg-[var(--color-bg-surface)] px-1.5 py-1 text-[10px] text-[var(--color-text-muted)]"
              title="Buduję podgląd strony"
            >
              <Loader2 size={12} className="animate-spin text-[var(--color-primary)]" />
            </span>
          )}

          {/* Ramki istniejącego tekstu — klikalne w trybie „Popraw tekst". */}
          {textMode &&
            (runsData?.runs ?? []).map((run) => (
              <button
                key={run.id}
                type="button"
                title={run.text}
                onClick={(e) => {
                  e.stopPropagation();
                  commitPending();
                  const current = edits.find((x) => x.id === run.id);
                  setPending({ x: run.x, y: run.y, h: run.h, value: (current ?? run).text, run });
                }}
                style={{
                  left: `${(run.x + moveOf(run.id).dx) * 100}%`,
                  top: `${(run.y + moveOf(run.id).dy) * 100}%`,
                  width: `${run.w * 100}%`, height: `${run.h * 100}%`,
                }}
                className={clsx(
                  "absolute border transition-colors",
                  editedIds.has(run.id)
                    ? "border-[var(--color-primary)] bg-[var(--color-primary-dim)]"
                    : "border-dashed border-[var(--color-border-strong)] hover:border-[var(--color-primary)] hover:bg-[var(--color-primary-dim)]",
                )}
              />
            ))}

          {/* Poprawiona treść widoczna od razu w miejscu starej — w KAŻDYM trybie,
              bo to robota czekająca na zapis, a nie ozdoba narzędzia. */}
          {editsHere.map((e) => (
            <span
              key={`edit-${e.id}`}
              className="pointer-events-none absolute flex items-center whitespace-pre bg-white leading-none text-black"
              style={{
                left: `${(e.x + moveOf(e.id).dx) * 100}%`,
                top: `${(e.y + moveOf(e.id).dy) * 100}%`,
                width: `${e.w * 100}%`, height: `${e.h * 100}%`,
                fontSize: fontPx(e.h),
                fontFamily: e.family ? fontCss(e.family) : undefined,
                fontWeight: e.bold ? 700 : 400,
                fontStyle: e.italic ? "italic" : "normal",
              }}
            >
              {e.text}
            </span>
          ))}

          {/* Obiekty strony (obrazy, rysunki, kreski) — widać je w trybie zaznaczania
              tak samo jak linie tekstu, więc da się je chwycić i skasować. */}
          {kind === "select" &&
            pageObjects.map((o) => {
              const b = objBounds(o);
              const moved = !!(objMoves[o.id]?.dx || objMoves[o.id]?.dy);
              return (
                <span
                  key={`obj-${o.id}`}
                  title={o.label}
                  style={{
                    left: `${b.x0 * 100}%`, top: `${b.y0 * 100}%`,
                    width: `${o.w * 100}%`, height: `${o.h * 100}%`,
                  }}
                  className={clsx(
                    "pointer-events-none absolute border border-dotted",
                    moved || picked.has(o.id)
                      ? "border-[var(--color-primary)] bg-[var(--color-primary-dim)]"
                      : "border-[var(--color-text-muted)]",
                  )}
                />
              );
            })}

          {/* Przesunięty obiekt: stare miejsce gaśnie, a w nowym pokazujemy WYCINEK
              obrazu strony — to, co wyjdzie w pliku, a nie sama ramka. */}
          {objMovedHere.map((id) => {
            const { obj: o, dx, dy } = objMoves[id];
            const sx = o.w >= 1 ? 0 : (o.x / (1 - o.w)) * 100;
            const sy = o.h >= 1 ? 0 : (o.y / (1 - o.h)) * 100;
            return (
              <span key={`objmv-${id}`}>
                <span
                  className="pointer-events-none absolute bg-white"
                  style={{
                    left: `${o.x * 100}%`, top: `${o.y * 100}%`,
                    width: `${o.w * 100}%`, height: `${o.h * 100}%`,
                  }}
                />
                <span
                  className="pointer-events-none absolute"
                  style={{
                    left: `${(o.x + dx) * 100}%`, top: `${(o.y + dy) * 100}%`,
                    width: `${o.w * 100}%`, height: `${o.h * 100}%`,
                    backgroundImage: pageSrc ? `url(${pageSrc})` : undefined,
                    backgroundSize: `${100 / Math.max(o.w, 0.001)}% ${100 / Math.max(o.h, 0.001)}%`,
                    backgroundPosition: `${sx}% ${sy}%`,
                  }}
                />
              </span>
            );
          })}

          {/* Usunięty obiekt: kropkowany ślad po tym, co zniknie z pliku. */}
          {objGoneHere.map((id) => {
            const o = objGone[id].obj;
            return (
              <span
                key={`objdel-${id}`}
                title={`Usunięte: ${o.label}`}
                className="pointer-events-none absolute border border-dotted border-[var(--color-primary)] bg-white"
                style={{
                  left: `${o.x * 100}%`, top: `${o.y * 100}%`,
                  width: `${o.w * 100}%`, height: `${o.h * 100}%`,
                }}
              />
            );
          })}

          {/* Linie dokumentu w trybie zaznaczania: widać, co da się chwycić,
              i widać, co już przestawiono. */}
          {kind === "select" &&
            pageRuns.map((run) => {
              const b = runBounds(run);
              const moved = !!(moves[run.id]?.dx || moves[run.id]?.dy);
              return (
                <span
                  key={`sel-run-${run.id}`}
                  title={run.text}
                  style={{
                    left: `${b.x0 * 100}%`, top: `${b.y0 * 100}%`,
                    width: `${run.w * 100}%`, height: `${run.h * 100}%`,
                  }}
                  className={clsx(
                    "pointer-events-none absolute border border-dashed",
                    moved || picked.has(run.id)
                      ? "border-[var(--color-primary)] bg-[var(--color-primary-dim)]"
                      : carried.has(run.id)
                        ? "border-transparent"
                        : "border-[var(--color-border-strong)]",
                  )}
                />
              );
            })}

          {/* Przestawiona linia: najpierw GASZONE są wszystkie stare miejsca, dopiero
              potem rysujemy wycinki strony w nowych. Na przemian jedna zasłonka
              zamalowywała wycinek sąsiadki i podgląd wyglądał na poszarpany. */}
          {movedHere.map((id) => {
            const run = moves[id].run;
            const pad = (run.h ?? 0.01) * 0.25;
            return (
              <span
                key={`gasz-${id}`}
                className="pointer-events-none absolute bg-white"
                style={{
                  left: `${(run.x - pad) * 100}%`, top: `${(run.y - pad) * 100}%`,
                  width: `${(run.w + 2 * pad) * 100}%`, height: `${(run.h + 2 * pad) * 100}%`,
                }}
              />
            );
          })}
          {movedHere.map((id) => {
            const run = moves[id].run;
            const m = moveOf(id);
            if (editedIds.has(id)) return null;   // treść poprawiona — rysuje ją podgląd poprawki
            const pad = (run.h ?? 0.01) * 0.25;
            const w = run.w + 2 * pad;
            const h = run.h + 2 * pad;
            const x = run.x - pad;
            const y = run.y - pad;
            const sx = w >= 1 ? 0 : (x / (1 - w)) * 100;
            const sy = h >= 1 ? 0 : (y / (1 - h)) * 100;
            return (
              <span
                key={`wyc-${id}`}
                className="pointer-events-none absolute"
                style={{
                  left: `${(x + m.dx) * 100}%`, top: `${(y + m.dy) * 100}%`,
                  width: `${w * 100}%`, height: `${h * 100}%`,
                  backgroundImage: pageSrc ? `url(${pageSrc})` : undefined,
                  backgroundSize: `${100 / Math.max(w, 0.001)}% ${100 / Math.max(h, 0.001)}%`,
                  backgroundPosition: `${sx}% ${sy}%`,
                }}
              />
            );
          })}

          {pageShapes.map((s) => render(s))}
          {draft && render(draft, true)}

          {/* Obrys zaznaczonych elementów — widać, co złapie przesunięcie i Delete. */}
          {kind === "select" &&
            pageShapes
              .filter((sh) => picked.has(sh.id))
              .map((sh) => {
                const b = boundsOf(sh);
                return (
                  <span
                    key={`sel-${sh.id}`}
                    className="pointer-events-none absolute border border-dashed border-[var(--color-primary)] bg-[var(--color-primary-dim)]"
                    style={{
                      left: `${b.x0 * 100}%`, top: `${b.y0 * 100}%`,
                      width: `${(b.x1 - b.x0) * 100}%`, height: `${(b.y1 - b.y0) * 100}%`,
                    }}
                  />
                );
              })}

          {/* Niesiona grupa (siatka + jej wiersze) dostaje JEDEN obrys — pięćdziesiąt
              osobnych ramek zamieniało podgląd w czerwoną sieczkę. */}
          {kind === "select" && carried.size > 0 && (() => {
            const b = groupBounds(new Set([...picked, ...carried]));
            return (
              <span
                className="pointer-events-none absolute border border-dashed border-[var(--color-primary)]"
                style={{
                  left: `${b.x0 * 100}%`, top: `${b.y0 * 100}%`,
                  width: `${(b.x1 - b.x0) * 100}%`, height: `${(b.y1 - b.y0) * 100}%`,
                }}
              />
            );
          })()}

          {/* Element pod kursorem — zanim klikniesz, widzisz, co złapiesz. */}
          {kind === "select" &&
            hover &&
            !picked.has(hover) &&
            pageShapes
              .filter((sh) => sh.id === hover)
              .map((sh) => {
                const b = boundsOf(sh);
                return (
                  <span
                    key={`hov-${sh.id}`}
                    className="pointer-events-none absolute border border-dotted border-[var(--color-primary)]"
                    style={{
                      left: `${b.x0 * 100}%`, top: `${b.y0 * 100}%`,
                      width: `${(b.x1 - b.x0) * 100}%`, height: `${(b.y1 - b.y0) * 100}%`,
                    }}
                  />
                );
              })}

          {/* Gumka — zaznaczanie prostokątem, jak w każdym edytorze graficznym. */}
          {band && (
            <span
              className="pointer-events-none absolute border border-[var(--color-primary)] bg-[var(--color-primary-dim)]"
              style={{
                left: `${Math.min(band.x0, band.x1) * 100}%`,
                top: `${Math.min(band.y0, band.y1) * 100}%`,
                width: `${Math.abs(band.x1 - band.x0) * 100}%`,
                height: `${Math.abs(band.y1 - band.y0) * 100}%`,
              }}
            />
          )}

          {/* Pole tekstowe stoi DOKŁADNIE tam, gdzie kliknięto. */}
          {pending && (
            <input
              ref={pendingRef}
              value={pending.value}
              onChange={(e) => setPending({ ...pending, value: e.target.value })}
              onMouseDown={(e) => e.stopPropagation()}
              onBlur={commitPending}
              onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === "Enter") commitPending();
                if (e.key === "Escape") setPending(null);
              }}
              aria-label={pending.run ? "Popraw tekst" : "Treść notatki"}
              placeholder={pending.run ? "" : "Wpisz treść…"}
              style={{
                left: `${pending.x * 100}%`,
                top: `${pending.y * 100}%`,
                minWidth: "12%",
                width: pending.run ? `${Math.max(pending.run.w * 100, 12)}%` : "24%",
                fontSize: fontPx(pending.h),
                color: pending.run ? "#000" : color,
                // Poprawiana linia pisze się KROJEM TEJ LINII (serwer go rozpoznaje
                // i tym samym zapisze w pliku) — inaczej podgląd kłamie i wygląda,
                // jakby edycja podmieniała krój na bezszeryfowy.
                fontFamily: pending.run
                  ? pending.run.family
                    ? fontCss(pending.run.family)
                    : undefined
                  : fontCss(font),
                fontWeight: (pending.run ? pending.run.bold : bold) ? 700 : 400,
                fontStyle: (pending.run ? pending.run.italic : italic) ? "italic" : "normal",
              }}
              className="absolute z-10 border border-[var(--color-primary)] bg-white px-0.5 leading-none outline-none"
            />
          )}
        </div>

        {(pageShapes.length > 0 || edits.length > 0) && (
          <ul className="mx-auto mt-3 max-w-[860px] space-y-1">
            {editsHere.map((e) => (
              <li
                key={`li-${e.id}`}
                className="flex items-center justify-between border border-[var(--color-primary)] bg-[var(--color-primary-dim)] px-2 py-1 text-[11px]"
              >
                <span className="flex items-center gap-2 truncate">
                  <TextCursorInput size={12} className="shrink-0 text-[var(--color-primary)]" />
                  <span className="truncate text-[var(--color-text-secondary)]">
                    Poprawka: {e.text}
                  </span>
                </span>
                <button
                  type="button"
                  aria-label="Cofnij poprawkę"
                  onClick={() => setEdits((all) => all.filter((x) => x.id !== e.id))}
                  className="text-[var(--color-text-muted)] hover:text-[var(--color-primary)]"
                >
                  <X size={12} />
                </button>
              </li>
            ))}
            {pageShapes.map((s) => (
              <li
                key={s.id}
                className="flex items-center justify-between border border-[var(--color-border)] bg-[var(--color-bg-surface)] px-2 py-1 text-[11px]"
              >
                <span className="flex items-center gap-2 truncate">
                  <span
                    className="inline-block h-3 w-3 border border-[var(--color-border)]"
                    style={{ background: s.type === "redact" ? "#000" : s.color }}
                  />
                  <span className="text-[var(--color-text-secondary)]">
                    {TOOLS.find((t) => t.kind === s.type)?.label ?? s.type}
                    {s.text ? `: ${s.text}` : ""}
                  </span>
                </span>
                <span className="flex shrink-0 items-center gap-1.5">
                  {s.type === "text" && (
                    <button
                      type="button"
                      aria-label={`Popraw notatkę: ${s.text ?? ""}`}
                      title="Popraw treść (albo kliknij dwa razy na stronie)"
                      onClick={() => editNote(s)}
                      className="text-[var(--color-text-muted)] hover:text-[var(--color-primary)]"
                    >
                      <Pencil size={12} />
                    </button>
                  )}
                  <button
                    type="button"
                    aria-label="Usuń element"
                    onClick={() => setShapes((all) => all.filter((x) => x.id !== s.id))}
                    className="text-[var(--color-text-muted)] hover:text-[var(--color-primary)]"
                  >
                    <X size={12} />
                  </button>
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
